import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { api } from '../services/api';
import { ROLE_LABELS, ROLE_LABEL_KEYS, normalizeRole } from '../config/permissions';
import toast from 'react-hot-toast';
import { PhoneIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

// Known backend `detail` strings for the invite lookup, mapped to i18n keys so
// an ES user never sees the raw English API text next to the translated heading
// (Oct-10 Amplify audit, shots/31-invite-bad-token.jpg). Unknown details still
// fall through verbatim (they carry tenant-specific context).
const INVITE_DETAIL_KEYS = {
  'invalid or expired invitation': 'invite.invalidOrExpired',
};
const translateInviteDetail = (t, detail) => {
  const key = INVITE_DETAIL_KEYS[String(detail || '').trim().toLowerCase()];
  if (key) return t(key);
  return detail || t('invite.invalidOrExpired');
};

function Invite() {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const googleLocale = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const inviteRole = inviteInfo?.role;
  const inviteRoleKey = ROLE_LABEL_KEYS[inviteRole] || ROLE_LABEL_KEYS[normalizeRole(inviteRole)];
  const inviteRoleLabel = inviteRoleKey
    ? t(inviteRoleKey)
    : ROLE_LABELS[inviteRole] || ROLE_LABELS[normalizeRole(inviteRole)] || inviteRole;

  const fetchInviteInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/team/invite/${token}`);
      setInviteInfo(response.data);
    } catch (fetchError) {
      console.error('Error fetching invite:', fetchError);
      setError(translateInviteDetail(t, fetchError.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchInviteInfo();
  }, [fetchInviteInfo]);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      if (!credentialResponse?.credential) {
        throw new Error('Missing Google credential response');
      }

      setSubmitting(true);
      const response = await api.post('/team/invite/accept-google', {
        token,
        google_token: credentialResponse.credential,
      });

      localStorage.setItem('access_token', response.data.access_token);
      toast.success(t('invite.registrationCompleteGoogle'));
      window.location.href = '/';
    } catch (googleError) {
      console.error('Error accepting invite with Google:', googleError);
      toast.error(googleError.response?.data?.detail || t('invite.googleSignInFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleError = () => {
    toast.error(t('invite.googleSignInFailed'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('errors.passwordsDontMatch'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('errors.passwordMinLength'));
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.post('/team/invite/accept', {
        token,
        password,
      });

      localStorage.setItem('access_token', response.data.access_token);
      toast.success(t('invite.registrationComplete'));
      window.location.href = '/';
    } catch (submitError) {
      console.error('Error accepting invite:', submitError);
      toast.error(submitError.response?.data?.detail || t('errors.failedCompleteReg'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full px-6">
          <div className="text-center">
            <XCircleIcon className="mx-auto h-16 w-16 text-red-500" />
            <h2 className="mt-4 text-2xl font-bold text-foreground">{t('invite.invalidInvitation')}</h2>
            <p className="mt-2" style={{ color: 'var(--muted)' }}>{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover"
            >
              {t('invite.goToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-primary rounded-xl flex items-center justify-center">
            <PhoneIcon className="h-10 w-10 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-foreground">
            {t('invite.youreInvited')}
          </h2>
          <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>
            <span dangerouslySetInnerHTML={{ __html: t('invite.joinTeam', { tenant: inviteInfo?.tenant_name }) }} />
          </p>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm" style={{ color: 'var(--muted)' }}>{t('common.name')}</span>
              <span className="text-sm font-medium text-foreground">{inviteInfo?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm" style={{ color: 'var(--muted)' }}>{t('common.email')}</span>
              <span className="text-sm font-medium text-foreground">{inviteInfo?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm" style={{ color: 'var(--muted)' }}>{t('common.role')}</span>
              <span className="text-sm font-medium text-foreground">{inviteRoleLabel}</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="w-full flex justify-center py-2.5 px-4 border border-border text-sm font-medium rounded-full text-foreground bg-card">
            {googleClientId ? (
              <GoogleOAuthProvider clientId={googleClientId}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  shape="pill"
                  width="320"
                  locale={googleLocale}
                />
              </GoogleOAuthProvider>
            ) : (
              <span>{t('invite.googleNotConfigured')}</span>
            )}
          </div>
          <p className="text-xs text-center" style={{ color: 'var(--muted)' }}>
            {t('invite.continueWithGoogle')}
          </p>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-2 bg-background" style={{ color: 'var(--muted)' }}>
              {t('invite.orUsePassword')}
            </span>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground">
                {t('invite.createPassword')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-border placeholder:text-muted bg-card text-foreground rounded-md focus:outline-none focus:ring-primary focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder={t('invite.enterPassword')}
                minLength={6}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-foreground">
                {t('invite.confirmPassword')}
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-border placeholder:text-muted bg-card text-foreground rounded-md focus:outline-none focus:ring-primary focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder={t('invite.confirmYourPassword')}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={submitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-full text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              {submitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('invite.completingRegistration')}
                </span>
              ) : (
                <span className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  {t('invite.completeRegistration')}
                </span>
              )}
            </button>
          </div>
        </form>

        <p className="text-center text-xs" style={{ color: 'var(--muted)' }}>
          {t('invite.alreadyHaveAccount')}{' '}
          <a href="/login" className="text-primary-600 hover:text-primary-500">
            {t('invite.signIn')}
          </a>
        </p>
      </div>
    </div>
  );
}

export default Invite;
